// contact-us.js
import React, { useState } from 'react';
import Location from '../assets/images/location.png';
import SEO from '../components/SEO';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    businessType: '',
    message: '',
    captchaValue: '',
  });
  const [captchaCode, setCaptchaCode] = useState(generateCaptcha());
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const businessTypes = [
    'Cafe',
    'Clothing & Fashion',
    'Computer & Electronics',
    'Cosmetics/Skincare/Perfume',
    'Grocery Store',
    'Gym & Fitness',
    'Hair & Beauty',
    'Laundry',
    'Pharmacy',
    'Quickserve/Kiosk/Foodtruck',
    'Restaurant',
    'Spa & Massage',
    'Home Business',
    'Other'
  ];

  function generateCaptcha() {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const refreshCaptcha = () => {
    setCaptchaCode(generateCaptcha());
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.captchaValue.toUpperCase() === captchaCode) {
      // Set loading state
      setIsSubmitting(true);
      
      // Send form data to the endpoint
      fetch("https://mmsbhost.mpay.my/api/mailer/vernpos/api.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          businessType: formData.businessType,
          message: formData.message,
          submittedAt: new Date().toISOString(),
        }),
      })
        .then((response) => {
          setIsSubmitting(false);
          if (response.ok) {
            // Successful form submission
            setSubmitStatus("success");
            setFormData({
              name: "",
              email: "",
              phoneNumber: "",
              businessType: "",
              message: "",
              captchaValue: "",
            });
            refreshCaptcha();
          } else {
            // Handle server errors
            setSubmitStatus("serverError");
          }
        })
        .catch((error) => {
          setIsSubmitting(false);
          console.error("Error submitting form:", error);
          setSubmitStatus("serverError");
        });
    } else {
      setSubmitStatus('error');
    }
  };

  return (
    <>
      <SEO 
        title="Contact VernPOS Malaysia | Get POS System Demo & Support"
        description="Need a POS system in Malaysia? Contact VernPOS for personalized demos, quotes, and support for retail, F&B, and salon businesses across Malaysia."
        keywords="POS system Malaysia, contact VernPOS, POS demo Malaysia, POS support Malaysia, cloud POS quote, F&B POS Malaysia"
        url="https://vernpos.my/contact-us"
      />
      <div className="max-w-6xl px-4 py-8 flex flex-col md:flex-row p-4 rounded-lg mx-auto my-8">
        <div className="bg-gray-100 p-6 rounded-lg flex-1 mr-0 md:mr-4 mb-4 md:mb-0">
          <div className="mb-6">
            <h5 className="text-gray-500 text-sm font-bold mb-2">
              Get in touch
            </h5>
            <h2 className="text-2xl font-bold mb-1">
              Let's Chat, Reach Out to Us
            </h2>
            <p className="text-gray-600 text-sm">
              Have a question or feedback? We're here to help. Send us a message
              and we'll respond within 24 hours.
            </p>
          </div>
          <div className="border-t border-gray-300 mb-6"></div>
          {submitStatus === "success" && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              Message sent successfully!
            </div>
          )}

          {submitStatus === "error" && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              Captcha verification failed. Please try again.
            </div>
          )}

          {submitStatus === "serverError" && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              Server error. Please try again later.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Name (required)
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
                placeholder="Enter your name"
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email (required)
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
                placeholder="Enter your email"
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number (required)
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
                placeholder="+60 1234 5678"
              />
            </div>

            <div className="mb-4">
              <label
                htmlFor="businessType"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Please Select Your Business Type
              </label>
              <select
                id="businessType"
                name="businessType"
                value={formData.businessType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="" disabled>
                  Select a business type
                </option>
                {businessTypes.map((type, index) => (
                  <option key={index} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label
                htmlFor="message"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                placeholder="Leave us your message..."
              ></textarea>
            </div>

            <div className="mb-4">
              <label
                htmlFor="captcha"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Captcha
              </label>
              <div className="flex items-center">
                <div className="bg-gray-200 p-2 rounded mr-2 w-24 text-center font-mono">
                  {captchaCode}
                </div>
                <button
                  type="button"
                  onClick={refreshCaptcha}
                  className="p-2 bg-gray-100 rounded"
                >
                  ↺
                </button>
              </div>
              <input
                type="text"
                id="captcha"
                name="captchaValue"
                value={formData.captchaValue}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md mt-2"
                required
                placeholder="Enter the code above"
              />
            </div>

            <button
              type="submit"
              className="bg-vern text-white py-2 px-6 rounded-full hover:bg-red-700 transition-colors flex items-center justify-center"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Sending...
                </>
              ) : (
                "Send Message"
              )}
            </button>
          </form>
        </div>

        {/* Right Column - Map and Contact Details */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-6 max-w-5xl mx-auto px-4">
          {/* 📍 MAP BOX */}
          <div className="rounded-2xl overflow-hidden">
            <img
              src={Location}
              alt="Location map"
              className="w-full h-64 md:h-full object-cover"
            />
          </div>

          {/* 📨 CONTACT DETAILS BOX */}
          <div className="bg-gray-100 p-6 md:p-8 rounded-2xl space-y-6">
            {/* Address */}
            <div className="bg-white flex items-start space-x-4 p-4 rounded-xl">
              <div className="bg-red-50 text-vern p-3 rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1 ">Address</h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Lot 109–113, Jalan USJ 21/10, 47630 Subang Jaya,
                  <br />
                  Selangor Darul Ehsan, Malaysia.
                </p>
              </div>
            </div>

            {/* Email */}
            <div className="bg-white flex items-start space-x-4 p-4 rounded-lg">
              <div className="bg-red-50 text-vern p-3 rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Email</h4>
                <p className="text-sm text-gray-600">info@vernpos.my</p>
              </div>
            </div>

            {/* Phone */}
            <div className="bg-white flex items-start space-x-4 p-4 rounded-lg">
              <div className="bg-red-50 text-vern p-3 rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-1">Phone</h4>
                <p className="text-sm text-gray-600">+60 1700 81 6729</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactUs;